
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
// Plan limits now handled server-side
// Helper functions
const countCampaignsInMonth = (campaigns, date = new Date()) => {
  if (!Array.isArray(campaigns)) return 0;
  const y = date.getFullYear();
  const m = date.getMonth();
  return campaigns.filter(c => {
    if (!c?.createdAt) return false;
    const d = new Date(c.createdAt);
    return d.getFullYear() === y && d.getMonth() === m;
  }).length;
};


const { FiMail, FiMessageSquare, FiSend, FiClock, FiUsers, FiRefreshCw, FiCalendar, FiTarget, FiZap } = FiIcons;

const CreateCampaign = () => {
  const { user } = useAuth();
  const [planId, setPlanId] = useState('free');
  const [limits, setLimits] = useState({ id: 'free', label: 'Free', campaignsPerMonth: 1 });
  const [loadingPlan, setLoadingPlan] = useState(true);
  // Direct plan fetch
  useEffect(() => {
    let active = true;
    const loadPlan = async () => {
      if (!user?.id) {
        setPlanId('free');
        setLimits({ id: 'free', label: 'Free', campaignsPerMonth: 1 });
        setLoadingPlan(false);
        return;
      }
      setLoadingPlan(true);
      try {
        const { data: subscription, error: subError } = await supabase
          .from('subscriptions')
          .select('plan_id')
          .eq('user_id', user.id)
          .maybeSingle();
        if (subError) throw subError;
        if (!subscription?.plan_id) {
          if (active) {
            setPlanId('free');
            setLimits({ id: 'free', label: 'Free', campaignsPerMonth: 1 });
          }
          return;
        }
        const { data: plan, error: planError } = await supabase
          .from('plans')
          .select('*')
          .eq('id', subscription.plan_id)
          .single();
        if (planError) throw planError;
        const name = plan?.name || 'Free';
        const lower = name.toLowerCase();
        let tier = { id: 'free', label: name, campaignsPerMonth: 1 };
        if (lower.includes('growth')) tier = { ...tier, id: 'growth', campaignsPerMonth: null };
        else if (lower.includes('pro')) tier = { ...tier, id: 'pro', campaignsPerMonth: null };
        if (active) {
          setPlanId(tier.id);
          setLimits(tier);
        }
      } catch (e) {
        if (active) {
          setPlanId('free');
          setLimits({ id: 'free', label: 'Free', campaignsPerMonth: 1 });
        }
      } finally {
        if (active) setLoadingPlan(false);
      }
    };
    loadPlan();
    return () => { active = false; };
  }, [user?.id]);
  const [fans, setFans] = useState([]);
  const [smartLinks, setSmartLinks] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [channel, setChannel] = useState('email');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [audience, setAudience] = useState('superfans');
  const [sendTime, setSendTime] = useState('now'); // 'now' | 'scheduled'
  const [scheduledDate, setScheduledDate] = useState(null); // 'YYYY-MM-DD' string
  const [scheduledTime, setScheduledTime] = useState(''); // start HH:MM
  const [scheduledTimeEnd, setScheduledTimeEnd] = useState(''); // optional end HH:MM
  const [campaignId, setCampaignId] = useState("");
  const [hasConsent, setHasConsent] = useState(false);
  const [includeComplianceFooter, setIncludeComplianceFooter] = useState(true);

  const computeComplianceFooter = (ch) => {
    if (ch === 'sms') {
      return 'Reply STOP to unsubscribe. Msg&Data rates may apply. HELP for help.';
    }
    return 'You are receiving this because you opted in, unsubscribe here.';
  };

  useEffect(() => {
    const fetchFans = async () => {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('fans')
        .eq('id', user.id)
        .single();
      if (error) {
        console.error('Error fetching fans:', error);
        return;
      }
      const fansArr = Array.isArray(profile?.fans)
        ? profile.fans.map(fanStr => {
            try {
              return JSON.parse(fanStr);
            } catch {
              return null;
            }
          }).filter(Boolean)
        : [];
      setFans(fansArr);
    };
    if (user?.id) fetchFans();
  }, [user?.id]);

  // Load user's smart links for selector
  useEffect(() => {
    const loadLinks = async () => {
      try {
  const res = await fetch(`https://backend.amply.buildfast.us/smart-links/user/${user.id}`);
        const data = await res.json();
        if (res.ok && data?.success && Array.isArray(data.data)) {
          setSmartLinks(data.data);
        }
      } catch (e) {
        console.warn('Failed loading smart links:', e?.message);
      }
    };
    if (user?.id) loadLinks();
  }, [user?.id]);

  useEffect(() => {
    if (!campaignId) {
      try {
        const id = (crypto && crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
        setCampaignId(id);
      } catch {
        setCampaignId(`${Date.now()}-${Math.random().toString(36).slice(2,8)}`);
      }
    }
  }, [campaignId]);

  const generateAIDraft = () => {
    const prompts = {
      email: "🎵 Hey there! I've got something special dropping this Friday that I can't wait for you to hear. Be among the first to stream my new single - hit that presave button now and get ready to vibe! 🎧✨",
      sms: "🚨 New single drops Friday! Be first to hear it - presave now: [link]"
    };
    setMessage(prompts[channel]);
    toast.success('AI draft generated!', {
      description: `Created a ${channel.toUpperCase()} message draft for your campaign.`
    });
  };

  const ScheduleCampaign = async () => {
    if (!audience || audience !== "fans") {
      toast.error('Please select an audience before sending the campaign.');
      return;
    }
    if (!hasConsent) {
      toast.error('You must confirm you have opt-in consent before sending.');
      return;
    }
    if (sendTime === 'scheduled') {
      if (!scheduledDate || !scheduledTime) {
        toast.error('Pick a date and start time.');
        return;
      }
      const [hh, mm] = scheduledTime.split(':');
      let local = null;
      if (typeof scheduledDate === 'string') {
        const [y,m,d] = scheduledDate.split('-').map(Number);
        local = new Date(y, (m||1)-1, d||1);
      } else if (scheduledDate instanceof Date) {
        local = new Date(scheduledDate);
      }
      if (!local) {
        toast.error('Invalid date selected');
        return;
      }
      local.setHours(parseInt(hh || '0', 10), parseInt(mm || '0', 10), 0, 0);
      const sched = local.getTime();
      const now = Date.now();
      if (isNaN(sched) || sched < now + 60 * 1000) {
        toast.error('Scheduled time must be at least 1 minute from now.');
        return;
      }
      const max = now + 7 * 24 * 60 * 60 * 1000;
      if (sched > max) {
        toast.error('Scheduled time must be within 7 days.');
        return;
      }
      // Validate optional end time
      if (scheduledTimeEnd) {
        const [eh, em] = scheduledTimeEnd.split(':');
        const end = new Date(local);
        end.setHours(parseInt(eh||'0',10), parseInt(em||'0',10), 0, 0);
        if (end.getTime() <= sched) {
          toast.error('End time must be after start time.');
          return;
        }
      }
    }
    try {
      const idToUse = campaignId || `${Date.now()}`;
      // Enforce campaign limit (e.g., Free plan 1 per month)
      const { data: existingCampaignsData } = await supabase
        .from('profiles')
        .select('campaigns')
        .eq('id', user.id)
        .single();
      const parsedCampaigns = Array.isArray(existingCampaignsData?.campaigns)
        ? existingCampaignsData.campaigns.map(c => { try { return JSON.parse(c); } catch { return null; } }).filter(Boolean)
        : [];
      const campaignsThisMonth = countCampaignsInMonth(parsedCampaigns);
      if (typeof limits.campaignsPerMonth === 'number' && campaignsThisMonth >= limits.campaignsPerMonth) {
        toast.error(`Campaign limit reached for ${limits.label} plan`, { description: `Your plan allows ${limits.campaignsPerMonth} campaign${limits.campaignsPerMonth===1?'':'s'} per month. Upgrade to send more.` });
        return;
      }
      // null or non-number => unlimited

      // Helper: append ?campaign=<id> to all URLs found in a text block
      const appendCampaignParamToUrls = (text, id) => {
        if (!text) return text;
        const urlRegex = /(https?:\/\/[^\s)]+)(\)?)/gi; // captures http/https links, avoids trailing )
        return text.replace(urlRegex, (match) => {
          try {
            // remove trailing punctuation commonly following links
            const trailing = /[).,!?:;]*$/.exec(match)[0];
            const core = match.slice(0, match.length - trailing.length);
            const u = new URL(core);
            u.searchParams.set('campaign', id);
            return u.toString() + trailing;
          } catch {
            return match; // if not a valid URL, leave unchanged
          }
        });
      };

      // 1. Fetch current campaigns from profiles table
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('campaigns, fans')
        .eq('id', user.id)
        .single();

      if (profileError) {
        toast.error('Failed to fetch profile data');
        return;
      }

      // 2. Get fans emails and phone numbers from fans row
      const fansArr = Array.isArray(profile.fans)
        ? profile.fans.map(fanStr => {
            try {
              return JSON.parse(fanStr);
            } catch {
              return null;
            }
          }).filter(Boolean)
        : [];
      const emails = fansArr.map(fan => fan.email).filter(email => !!email);
      const phoneNumbers = fansArr.map(fan => fan.phone).filter(phone => !!phone);

      // 3. Build campaign object (include recipients/messages_count derived from audience)
      const recipientsCount = channel === 'email' ? emails.length : phoneNumbers.length;
      const campaignObj = {
        type: channel,
        subject,
        message,
        id: idToUse,
        createdAt: new Date().toISOString(),
        recipientsCount,
        messages_count: recipientsCount,
        // Metrics to be tracked and updated by backend
        clicks: 0,
        bounceRate: 0,
        avgTimeSpent: 0,
        conversionRate: 0,
        newFans: 0,
        openRate: 0,
        deliveryRate: 100,
      };
      const campaignString = JSON.stringify(campaignObj);

      // 4. Update campaigns row (append new campaign)
      const campaignsArr = Array.isArray(profile.campaigns) ? [...profile.campaigns, campaignString] : [campaignString];
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ campaigns: campaignsArr })
        .eq('id', user.id);

      if (updateError) {
        toast.error('Failed to save campaign');
        return;
      }

      // 5. Rewrite message to include campaign parameter on any URLs
  let rewrittenMessage = appendCampaignParamToUrls(message, idToUse);

      // 5b. Append compliance footer if selected and not already present
      if (includeComplianceFooter) {
        const footer = computeComplianceFooter(channel);
        if (footer && !rewrittenMessage.toLowerCase().includes(footer.slice(0,10).toLowerCase())) {
          rewrittenMessage = `${rewrittenMessage}\n\n${footer}`;
        }
      }

      // 6. Send campaign using correct channel
      // Base payload; backend should schedule if schedule_at provided (ISO string)
      let scheduleISO = null;
      let scheduleEndISO = null;
      if (sendTime === 'scheduled' && scheduledDate && scheduledTime) {
        const [hh, mm] = scheduledTime.split(':');
        let local = null;
        if (typeof scheduledDate === 'string') {
          const [y,m,d] = scheduledDate.split('-').map(Number);
          local = new Date(y, (m||1)-1, d||1);
        } else if (scheduledDate instanceof Date) {
          local = new Date(scheduledDate);
        }
        if (local) {
          local.setHours(parseInt(hh || '0', 10), parseInt(mm || '0', 10), 0, 0);
          scheduleISO = new Date(local.getTime()).toISOString();
          if (scheduledTimeEnd) {
            const [eh, em] = scheduledTimeEnd.split(':');
            const end = new Date(local);
            end.setHours(parseInt(eh||'0',10), parseInt(em||'0',10), 0, 0);
            if (end.getTime() > local.getTime()) {
              scheduleEndISO = end.toISOString();
            }
          }
        }
      }
      const base = { userId: user.id, campaignId: idToUse, message: rewrittenMessage, schedule_at: scheduleISO, schedule_end: scheduleEndISO };
      let response;
      if (channel === 'sms') {
  response = await fetch('https://backend.amply.buildfast.us/send-campaign', {
          method: 'POST',
        headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ channel: 'sms', phoneNumbers, ...base }),
        });
      } else {
  response = await fetch('https://backend.amply.buildfast.us/send-campaign', {
          method: 'POST',
        headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ channel: 'email', emails, subject, ...base }),
        });
      }
      if (!response.ok) {
        toast.error('Failed to send campaign');
        return;
      }
      toast.success(sendTime === 'scheduled' ? 'Campaign scheduled!' : 'Campaign sent successfully!');
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (err) {
      console.error(err);
      toast.error('An error occurred while sending campaign');
    }
  }


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-200">Create Campaign</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">Send targeted messages to your fans</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Campaign Builder */}
        <div className="lg:col-span-2 space-y-6">
          {/* Channel Selection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-black rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700"
          >
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-4">Select Channel</h2>
            <div className="grid grid-cols-2 gap-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setChannel('email')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  channel === 'email'
                    ? 'border-[#ebcebc] bg-[#ebcebc]/20 dark:bg-[#ebcebc]/20 text-slate-900 dark:text-slate-200'
                    : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                }`}
              >
                <SafeIcon icon={FiMail} className="text-2xl mx-auto mb-2 text-[#ebcebc]" />
                <p className="font-medium text-slate-800 dark:text-slate-200">Email</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Rich content, high engagement</p>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setChannel('sms')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  channel === 'sms'
                    ? 'border-[#ebcebc] bg-[#ebcebc]/20 dark:bg-[#ebcebc]/20 text-slate-900 dark:text-slate-200'
                    : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                }`}
              >
                <SafeIcon icon={FiMessageSquare} className="text-2xl mx-auto mb-2 text-[#ebcebc]" />
                <p className="font-medium text-slate-800 dark:text-slate-200">SMS</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Direct, immediate reach</p>
              </motion.button>
            </div>
          </motion.div>

          {/* Subject Line */}
          {channel === 'email' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-black rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700"
            >
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-4">Subject Line</h2>
              <input
                type="text"
                placeholder="Enter your email subject..."
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full p-3 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-black text-slate-900 dark:text-white"
              />
            </motion.div>
          )}

          {/* Message Editor */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-black rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">Message</h2>
            </div>
            <textarea
              rows={8}
              placeholder={`Write your ${channel.toUpperCase()} message here...`}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full p-3 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-black text-slate-900 dark:text-white resize-none"
            />
            <div className="flex justify-between items-center mt-3">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {message.length} characters {channel === 'sms' && '(160 char limit recommended)'}
              </p>
              <div className="flex space-x-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  onClick={generateAIDraft}
                  className="text-[#ebcebc] hover:text-[#ebcebc] text-sm font-medium flex items-center space-x-1"
                >
                  <SafeIcon icon={FiRefreshCw} />
                  <span>Regenerate</span>
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* Audience Selection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-black rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700"
          >
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-4">Select Audience</h2>
            <div className="space-y-3">
                <motion.label
                  whileHover={{ scale: 1.01 }}
                  className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all ${
              'border-[#ebcebc] bg-[#ebcebc]/20 dark:bg-[#ebcebc]/20 text-slate-900 dark:text-slate-200'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="audience"
                      value="fans"
                      checked={audience === "fans"}
                      onChange={(e) => setAudience(e.target.value)}
                      className="text-[#ebcebc] focus:ring-[#ebcebc]"
                    />
                    <div>
                      <p className="font-medium text-slate-800 dark:text-slate-200 capitalize">My Fans</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Send targeted messages to your fans</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-[#ebcebc]">{fans?.length || 0}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">fan(s)</p>
                  </div>
                </motion.label>
            </div>

            {/* Smart Link Selector */}
            <div className="mt-6">
              <h3 className="text-md font-semibold text-slate-800 dark:text-slate-200 mb-2">Attach Smart Link</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">Choose a smart link to include in your message. We’ll add a campaign parameter for tracking.</p>
              <select
                value={selectedSlug}
                onChange={(e) => {
                  const slug = e.target.value;
                  setSelectedSlug(slug);
                  if (!slug) return;
                  // Build absolute URL to the public link with campaign param
                  const base = (import.meta?.env?.VITE_CLIENT_URL) || window.location.origin;
                  const url = `${String(base).replace(/\/$/, '')}/${slug}?campaign=${encodeURIComponent(campaignId || '')}`;
                  // Append to message if not already contained
                  if (!message.includes(url)) {
                    const sep = message && !/\s$/.test(message) ? '\n' : '';
                    setMessage(prev => `${prev || ''}${sep}${url}`);
                  }
                }}
                className="w-full p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-black text-slate-900 dark:text-white"
              >
                <option value="">Select a smart link…</option>
                {smartLinks.map((l) => (
                  <option key={l.slug} value={l.slug}>
                    {l.title} ({l.slug})
                  </option>
                ))}
              </select>
            </div>
          </motion.div>

          {/* Send Options */}
          {/* Compliance & Consent */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-black rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700"
          >
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-4">Compliance & Consent</h2>
            <div className="space-y-4">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={hasConsent}
                  onChange={(e) => setHasConsent(e.target.checked)}
                  className="mt-1 accent-[#ebcebc]"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  I confirm I have explicit, documented opt-in consent from all selected recipients to receive {channel.toUpperCase()} communications. I understand I must honor all opt-out / unsubscribe requests immediately.
                </span>
              </label>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={includeComplianceFooter}
                  onChange={(e) => setIncludeComplianceFooter(e.target.checked)}
                  className="mt-1 accent-[#ebcebc]"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  Append a required {channel.toUpperCase()} compliance footer (recommended & often legally required).
                </span>
              </label>
              <div className="text-xs space-y-2 bg-slate-50 dark:bg-white/10 p-3 rounded-md border border-slate-200 dark:border-slate-600">
                <p className="font-medium text-slate-800 dark:text-slate-200">Regulatory Notes</p>
                {channel === 'sms' ? (
                  <ul className="list-disc pl-5 space-y-1 text-slate-600 dark:text-slate-400">
                    <li>Ensure TCPA compliance: prior express written consent for marketing texts.</li>
                    <li>Always include opt-out instructions (e.g., "Reply STOP to unsubscribe").</li>
                    <li>Honor STOP/UNSUBSCRIBE keywords immediately.</li>
                    <li>Respect quiet hours where applicable.</li>
                  </ul>
                ) : (
                  <ul className="list-disc pl-5 space-y-1 text-slate-600 dark:text-slate-400">
                    <li>Include a visible unsubscribe link in every marketing email (e.g., CAN-SPAM, GDPR).</li>
                    <li>Identify sender clearly with a physical mailing address where required.</li>
                    <li>Only email users who have opted in or where you have legitimate interest.</li>
                    <li>Honor unsubscribe requests within required timeframes.</li>
                  </ul>
                )}
                {includeComplianceFooter && (
                  <p className="text-slate-500 dark:text-slate-400 italic">
                    Footer Preview: {computeComplianceFooter(channel)}
                  </p>
                )}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-black rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700"
          >
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-4">Send Options</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                onClick={() => setSendTime('now')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  sendTime === 'now'
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-slate-900 dark:text-slate-200'
                    : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                }`}
              >
                <SafeIcon icon={FiSend} className="text-2xl mx-auto mb-2 text-green-600" />
                <p className="font-medium text-slate-800 dark:text-slate-200">Send Now</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Immediate delivery</p>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                onClick={() => setSendTime('scheduled')}
                className={`p-4 rounded-lg border-2 transition-all ${
                  sendTime === 'scheduled'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-slate-900 dark:text-slate-200'
                    : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                }`}
              >
                <SafeIcon icon={FiClock} className="text-2xl mx-auto mb-2 text-blue-600" />
                <p className="font-medium text-slate-800 dark:text-slate-200">Schedule</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Pick a date & time</p>
              </motion.button>
            </div>
            {sendTime === 'scheduled' && (
              <div className="mt-4 space-y-4">
     <div className="space-y-3">
      <label className="text-sm font-medium text-gray-900 dark:text-white">Pick Date</label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-start text-left font-normal h-auto px-4 py-3 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
          >
            <div className="flex items-center gap-3 w-full">
              <div className="w-10 h-10 bg-blue-50 dark:bg-blue-500/10 rounded-lg flex items-center justify-center group-hover:bg-blue-100 dark:group-hover:bg-blue-500/20 transition-colors">
                <SafeIcon icon={FiCalendar} className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white">
                  {(() => {
                    if (!scheduledDate) return "Select date"
                    let localDate
                    if (typeof scheduledDate === "string") {
                      const [y, m, d] = scheduledDate.split("-").map(Number)
                      localDate = new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0)
                    } else if (scheduledDate instanceof Date) {
                      localDate = new Date(scheduledDate.getFullYear(), scheduledDate.getMonth(), scheduledDate.getDate())
                    }
                    if (!localDate || isNaN(localDate.getTime())) return "Select date"
                    return localDate.toLocaleDateString("en-US", {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  })()}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Click to choose date</div>
              </div>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
            <Calendar
              mode="single"
              selected={scheduledDate ? (() => {
                if (typeof scheduledDate === "string") {
                  const [y, m, d] = scheduledDate.split("-").map(Number)
                  return new Date(y, (m || 1) - 1, d || 1)
                }
                return scheduledDate
              })() : undefined}
              onSelect={(date) => {
                if (date) {
                  // Convert to YYYY-MM-DD string to avoid timezone issues
                  const y = date.getFullYear()
                  const m = String(date.getMonth() + 1).padStart(2, '0')
                  const d = String(date.getDate()).padStart(2, '0')
                  const dateStr = `${y}-${m}-${d}`
                  
                  // Validate within 7 days
                  const maxAllowed = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                  if (date.getTime() > maxAllowed.getTime()) {
                    toast.error('Date must be within 7 days')
                    return
                  }
                  
                  setScheduledDate(dateStr)
                  
                  // Auto-set time if empty
                  if (!scheduledTime) {
                    const soon = new Date(Date.now() + 10 * 60 * 1000)
                    setScheduledTime(
                      `${String(soon.getHours()).padStart(2, "0")}:${String(soon.getMinutes()).padStart(2, "0")}`
                    )
                  }
                }
              }}
              disabled={(date) => {
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                const maxDate = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
                return date < today || date > maxDate
              }}
              initialFocus
              className="rounded-md border-0"
           
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>

    {/* Time Picker */}
    <div className="space-y-3">
      <label className="text-sm font-medium text-gray-900 dark:text-white">Pick Time Range (local)</label>
      <div className='flex gap-4'>
        <div className='flex flex-col gap-2 flex-1'>
          <Label htmlFor='time-from' className='text-xs px-1 text-gray-600 dark:text-gray-300'>From</Label>
          <Input
            type='time'
            id='time-from'
            step='60'
            value={scheduledTime}
            onChange={(e)=> setScheduledTime(e.target.value)}
            className='text-black dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 appearance-none [&::-webkit-calendar-picker-indicator]:opacity-100'
          />
        </div>
        <div className='flex flex-col gap-2 flex-1'>
          <Label htmlFor='time-to' className='text-xs px-1 text-gray-600 dark:text-gray-300'>To (optional)</Label>
          <Input
            type='time'
            id='time-to'
            step='60'
            value={scheduledTimeEnd}
            onChange={(e)=> setScheduledTimeEnd(e.target.value)}
            className='text-black dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 appearance-none [&::-webkit-calendar-picker-indicator]:opacity-100'
          />
        </div>
      </div>
      <p className='text-xs text-gray-500 dark:text-gray-400'>End time is optional. If provided, campaign window will be sent between start and end.</p>
    </div>

    {/* Info Panel */}
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4">
      <div className="flex items-start gap-3">
        <div className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
            Scheduling Information
          </p>
          <p className="text-xs text-blue-700 dark:text-blue-300">
            <span className="font-medium">Local timezone:</span> {Intl.DateTimeFormat().resolvedOptions().timeZone}
          </p>
          <p className="text-xs text-blue-700 dark:text-blue-300">
            Must be 1+ minute from now and within 7 days (max).
          </p>
        </div>
      </div>
    </div>
  </div>
            )}

          </motion.div>
        </div>

        {/* Campaign Preview & Stats */}
        <div className="space-y-6">
          {/* Preview */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-black rounded-xl p-6 shadow-sm border border-slate-200 dark:border-slate-700"
          >
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-4">Preview</h3>
            <div className="bg-slate-50 dark:bg-white/20 rounded-lg p-4 space-y-3">
              {channel === 'email' && subject && (
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Subject</p>
                  <p className="font-medium text-slate-800 dark:text-slate-200">{subject}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Message</p>
                <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                  {message || `Your ${channel.toUpperCase()} message will appear here...`}
                </p>
                {includeComplianceFooter && (
                  <p className="mt-4 text-[11px] leading-snug text-slate-500 dark:text-slate-400 border-t pt-3 border-slate-200 dark:border-slate-600">
                    {computeComplianceFooter(channel)}
                  </p>
                )}
              </div>
            </div>
          </motion.div>

   
          {/* Send Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="w-full border-[#ebcebc] text-black dark:text-white border-2 rounded-lg bg-[#ebcebc]/20 dark:bg-[#ebcebc]/60 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center space-x-2"
            onClick={ScheduleCampaign}
          >
            <SafeIcon icon={sendTime === 'now' ? FiSend : FiClock} />
            <span>{sendTime === 'now' ? 'Send Campaign' : 'Schedule Campaign'}</span>
          </motion.button>


        </div>
      </div>
    </div>
  );
};

export default CreateCampaign;